import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"
import { FormikSwitch, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { User } from "../../../../../users/userTypes"
import { Box, FormHelperText, Grid, Typography } from "@mui/material"
import { useDateTime } from "../../../../../localization/useDateTime"
import { CreateEditTenantUserFormValues } from "../tenantUserTypes"
import { WorkgroupsMembershipPanel } from "./WorkgroupsMembershipPanel"

type CreateUpdateTenantAdminContentProps = Readonly<{
    mode: "create" | "update"
    existingUser?: User
}>

export function CreateUpdateTenantUserContent({ mode, existingUser }: CreateUpdateTenantAdminContentProps) {
    const dateTimeFactory = useDateTime()

    return (
        <StandardForm>
            <StandardMainPanel>
                <LocalizedStrict id="office-manager-create-user-field-id" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantUserFormValues> name="employeeId" label="Employee ID" fullWidth />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-name" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantUserFormValues>
                        required
                        name="displayName"
                        label="Name"
                        autoComplete="name"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-email" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantUserFormValues>
                        required
                        name="email"
                        label="Email"
                        autoComplete="email"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-mobile" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantUserFormValues>
                        name="mobileNumber"
                        label="Mobile"
                        autoComplete="mobile"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-phone" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantUserFormValues> name="phoneNumber" label="Phone no." fullWidth />
                </LocalizedStrict>
            </StandardMainPanel>
            <LocalizedStrict id="office-manager-settings-panel" attrs={{ title: true }}>
                <StandardSidePanel title="Settings">
                    {mode === "update" && (
                        <LocalizedStrict id="office-manager-create-user-field-active">
                            <FormikSwitch<CreateEditTenantUserFormValues> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                    )}
                    <LocalizedStrict id="office-manager-settings-send-invitation">
                        <FormikSwitch<CreateEditTenantUserFormValues> name="sendEmailInvitation">
                            Send email invitation
                        </FormikSwitch>
                    </LocalizedStrict>
                    <Box>
                        <LocalizedStrict id="office-manager-settings-disable-sync">
                            <FormikSwitch<CreateEditTenantUserFormValues> name="ignoreUserSyncJobs">
                                Disable synchronization
                            </FormikSwitch>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-manager-settings-disable-sync-text">
                            <FormHelperText>
                                When disabled this user will be ignored by user synchronization jobs
                            </FormHelperText>
                        </LocalizedStrict>
                    </Box>
                </StandardSidePanel>
            </LocalizedStrict>

            {(mode === "create" || existingUser) && (
                <LocalizedStrict id="company-user-workgroups-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Workgroups">
                        <WorkgroupsMembershipPanel />
                    </StandardSidePanel>
                </LocalizedStrict>
            )}

            {existingUser && existingUser.history !== undefined && (
                <LocalizedStrict id="history-card" attrs={{ title: true }}>
                    <StandardSidePanel title="history">
                        <Grid container rowGap={3}>
                            <Grid container item>
                                <Grid item xs={12} sm={5} justifyContent="space-between">
                                    <LocalizedStrict id="history-created">
                                        <Typography variant="body2">Created:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(existingUser.history.created.timestamp),
                                        )}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {existingUser.history.created.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container item>
                                <Grid item xs={12} sm={5}>
                                    <LocalizedStrict id="history-edited">
                                        <Typography variant="body2">Edited:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {existingUser.history.updated
                                            ? dateTimeFactory.formatDateTime(
                                                  dateTimeFactory.fromApi(existingUser.history.updated.timestamp),
                                              )
                                            : "-"}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {existingUser.history.updated?.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}
        </StandardForm>
    )
}
